import Deal from '@r6digital/rapidstor-types/types/Deal';
import { DescriptorCriteria } from '@r6digital/rapidstor-types/types/Descriptor';
import UnitType from '@r6digital/rapidstor-types/types/UnitType';

interface RapidStorOptions {
    baseURL: string;
    token?: string;
}

export const options: RapidStorOptions = {
    baseURL: import.meta.env.VITE_RAPIDSTOR_URL || 'https://prod.rapidstorapp.com/',
    token: import.meta.env.VITE_RAPIDSTOR_API_TOKEN,
};

export type RapidStorResponse<T = void> = {
    operation_message: string;
    operation_result: string;
    results: T;
};

export type RapidStorUnitSortOrder = 'size' | 'price' | 'availability' | 'floor' | 'none';

export interface RapidStorLocation {
    SiteID: number;
    sLocationCode: string;
    iGMTTimeOffset: number;
    sContactName: string | null;
    sEmailAddress: string | null;
    sSitePhone: string | null;
    sWebSiteURL: string | null;
    sSiteName: string;
    friendlyName: string | null;
    sSiteAddr1: string | null;
    sSiteAddr2: string | null;
    sSitePostalCode: string | null;
    sSiteRegion: string | null;
    sSiteCity: string | null;
    sSiteCountry: string | null;
    dcLatitude: string;
    dcLongitude: string;
    deleted: boolean;
    bArchived: string;
    rapidstorEnabled: boolean;
    token: string;
    dCreated: string;
    dUpdated: string;
    rapidstor: {
        groundFloor: number;
        unitListing: {
            useMultilevel: boolean;
            sortOrder: {
                primary: RapidStorUnitSortOrder;
                secondary: RapidStorUnitSortOrder;
                tertiary: RapidStorUnitSortOrder;
            };
        };
        includeReservedUnitsInOccupied: boolean;
        siteLogo: string;
        billingDefault: string;
        sizeSymbol: string;
        areaSizeSymbol: string;
        allowMoveIn: boolean;
        showAreaInsteadOfDimensions: boolean;
        completeMoveinUrl?: string;
        completeMoveinURL?: string;
        reservationNumberOfDaysInAdvanceLimit: number;
        moveinNumberOfDaysInAdvanceLimit: number;
        [key: string]: number | string | boolean | object | undefined | null;
    };
    hours: {
        officeMondayOpen: string;
        officeTuesdayOpen: string;
        officeWednesdayOpen: string;
        officeThursdayOpen: string;
        officeFridayOpen: string;
        officeSaturdayOpen: string;
        officeSundayOpen: string;
        officeMondayClose: string;
        officeTuesdayClose: string;
        officeWednesdayClose: string;
        officeThursdayClose: string;
        officeFridayClose: string;
        officeSaturdayClose: string;
        officeSundayClose: string;
        [key: string]: number | string;
    };
    [key: string]: number | string | boolean | object | undefined | null;
}

export interface RapidStorUnitType {
    SiteID: number;
    UnitID_FirstAvailable: number;
    UnitID_Representative: number;
    UnitTypeID: number;
    bAlarm: boolean;
    bChargeTax1: boolean;
    bChargeTax2: boolean;
    bClimate: boolean;
    bExcludeFromInsurance: boolean;
    bInside: boolean;
    bMobile: boolean;
    bPower: boolean;
    bPreferredIsPushRate: boolean;
    dcArea: number;
    dcLength: number;
    dcPreferredRate: number;
    dcPushRate: number;
    dcPushRate_NotRounded: number;
    dcReservationFee: number;
    dcStdRate: number;
    dcStdWeeklyRate: number;
    dcTax1Rate_Rent: number;
    dcTax2Rate_Rent: number;
    dcWebRate: number;
    dcWidth: number;
    deals: RapidStorDeal[];
    descriptor: RapidStorDescriptor;
    deleted: boolean;
    hasAvailableUnits: boolean;
    hasComputedAvailable: boolean;
    iDefLeaseNum: number;
    iFloor: number;
    iTotalOccupied: number;
    iTotalReserved: number;
    iTotalUnits: number;
    iTotalVacant: number;
    rates: RapidStorRates;
    sCorpCode: string;
    sLocationCode: string;
    sTypeName: string;
    sUnitName_FirstAvailable: string;
    sUnitName_Representative: string;
    units: unknown[];
    _id: string;
}

export interface RapidStorDescriptor {
    _id: string;
    sLocationCode: string;
    sCorpCode: string;
    name: string;
    description: string;
    picture: string;
    slides: string[];
    defaultInsuranceCoverage: number;
    deals: string[];
    criteria: {
        includes: DescriptorCriteria;
        excludes: DescriptorCriteria;
    };
    enabled: boolean;
    hidden: boolean;
    ordinalPosition: number;
    unitTypes: RapidStorUnitType[];
}

export interface RapidStorDeal {
    _id: string;
    customFormat: unknown;
    descriptions: string[];
    discountPlan: RapidStorDiscountPlan | null;
    fromDate: string | null;
    rates: RapidStorRates;
    title: string;
    toDate: string | null;
}

export interface RapidStorRates {
    discountIsRounded: boolean;
    discountName?: string;
    discountSaved: number | null;
    discountType: string;
    discounted: boolean;
    discountedRate: number | null;
    discountedRateUnrounded: number | null;
    prepaidAmount: number | null;
    prepaidDiscount: boolean;
    prepaidSaved: number | null;
    rate: number;
    rateUnrounded: number;
    tax1: number;
    tax2: number;
    taxInclusive: boolean;
    usingPushRate: boolean;
    usingWebRate: boolean;
}

export interface RapidStorDiscountPlan {
    _id: string;
    ConcessionID: number;
    sPlanName: string;
}

export interface RapidStorUnitList {
    unitTypes: RapidStorUnitType[];
    descriptors: RapidStorDescriptor[];
}

export interface RapidStorUnitListRequest {
    locationCode: string;
    personaId: string | null;
}

export interface RapidStorInsurance {
    hidden: boolean;
    maxArea: string | null;
    helpfulText: string;
    deleted: boolean;
    _id: string;
    InsurCoverageID: number;
    sLocationCode: string;
    SiteID: number;
    changeToken: string;
    changedAt: string;
    dcCoverage: number;
    dcPremium: number;
    sCoverageDesc: string;
    sProvidor: string;
}

export interface RapidStorPaymentResult {
    error: boolean | null;
    code: string | null;
    message: string | null;
}

export interface RapidStorReservationRequest {
    locationCode: string;
    payload: ReservationPayload;
}

export interface RapidStorPersona {
    _id: string;
    enabled: boolean;
    name: string;
    parameters: RapidStorPersonaParameter[];
    deals: RapidStorDeal[];
}

export interface RapidStorPersonaParameter {
    name: string;
    value: string;
}

export interface ReservationPayload {
    deal?: string | null;
    customListing: boolean;
    neededDate?: string; // ISO date string
    timezoneOffset?: number;
    sourceUrl: string;
    tenant: {
        tenantId: number | null;
        firstName: string; // use an empty string if not known
        lastName: string; // ^
        phone: string; // ^
        email: string; // ^
    };
    trackingId?: string;
    unitToUse?: string; // Unit._id - optional, if not specified RapidStor will choose for us
    unitType: string; // UnitType._id
    persona?: string | undefined;
}

export interface RapidStorReservation {
    deal: Deal;
    email: string;
    enquiry: boolean;
    firstName: string;
    lastName: string;
    phone: string;
    addr1: string;
    addr2: string;
    city: string;
    state: string;
    country: string;
    mobile: string;
    postalCode: string;
    declaredInsuranceAmt: number | null;
    sUnitName: string;
    tenantId: number | null;
    token: string;
    _id: string;
    unitType: UnitType;
    moveInContext: {
        neededDate: string;
    };
    ledgerResult?: {
        esign: {
            url: string;
        };
    };
    UnitID: number | null;
    ledgerId: number | null;
    waitingId: number | null;
    globalWaitingNum: number | null;
    persona: string | null;
}
